import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ObjectiveCategory, ObjectiveLevelOfEffort, ObjectivePriority, ObjectiveReminder } from '@contracts';
import { Briefcase, HeartPulse, Plane, Hammer, Landmark, Users, Check, ChevronDown } from '@ui/shared/icons';

/**
 * Shared building blocks for the desktop Objective create/edit screen
 * (design/screens/objective-create-desktop.md SS4/SS5). Every visual
 * decision here (selected-state recipe, color mapping) follows that spec's
 * SS3 token-mapping table byte-for-byte — no hardcoded colors, no new
 * selection idiom beyond the one "tinted fill, no left border" recipe reused
 * across category pills / effort tiles / segmented controls.
 */

// ---------------------------------------------------------------------------
// Category pill-select (SS4.3) — six values, own accent family
// ---------------------------------------------------------------------------

/** Exported so other screens (e.g. the Objectives catalog's category filter)
 *  reuse this exact category/icon/token list rather than a second, driftable
 *  copy of it. */
export const CATEGORY_OPTIONS: ReadonlyArray<{
  readonly value: ObjectiveCategory;
  readonly label: string;
  readonly icon: typeof Briefcase;
  readonly tokenPrefix: string;
}> = [
  { value: 'career', label: 'Career', icon: Briefcase, tokenPrefix: '--accent-obj-career' },
  { value: 'health', label: 'Health', icon: HeartPulse, tokenPrefix: '--accent-obj-health' },
  { value: 'travel', label: 'Travel', icon: Plane, tokenPrefix: '--accent-obj-travel' },
  { value: 'craft', label: 'Craft', icon: Hammer, tokenPrefix: '--accent-obj-craft' },
  { value: 'finance', label: 'Finance', icon: Landmark, tokenPrefix: '--accent-obj-finance' },
  { value: 'relationships', label: 'Relationships', icon: Users, tokenPrefix: '--accent-obj-relationships' },
];

/**
 * Resolves an `ObjectiveCategory` (or `null`, uncategorized) to its
 * `--accent-obj-*` token prefix — shared lookup so every consumer of
 * `CATEGORY_OPTIONS` (Priority chips, Next-up rows, Pinned Objectives, the
 * Objectives catalog filter) reads the same mapping instead of each
 * re-implementing its own `.find()`.
 */
export function objectiveCategoryTokenPrefix(category: ObjectiveCategory | null): string | null {
  if (category === null) return null;
  return CATEGORY_OPTIONS.find((option) => option.value === category)?.tokenPrefix ?? null;
}

export function CategoryPillSelect({
  value,
  onChange,
}: {
  readonly value: ObjectiveCategory | null;
  readonly onChange: (next: ObjectiveCategory) => void;
}) {
  return (
    <div>
      <div role="radiogroup" aria-label="Category" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {CATEGORY_OPTIONS.map((option) => {
          const selected = value === option.value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Category: ${option.label}`}
              onClick={() => onChange(option.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                minHeight: 'var(--touch-target-min)',
                padding: '0 var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: selected ? `1px solid var(${option.tokenPrefix}-vivid)` : '1px solid var(--border-subtle)',
                background: selected
                  ? `color-mix(in srgb, var(${option.tokenPrefix}-vivid) 14%, var(--surface-raised))`
                  : 'var(--surface-raised)',
                color: selected ? `var(${option.tokenPrefix})` : 'var(--text-secondary)',
                fontSize: 'var(--type-caption-size)',
                fontWeight: 500,
              }}
            >
              <Icon size={16} color={selected ? `var(${option.tokenPrefix})` : 'var(--text-muted)'} aria-hidden="true" />
              {option.label}
            </button>
          );
        })}
      </div>
      {value === null && (
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          Add a category any time — it just helps you scan your objectives later.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Effort tile selector (SS5.1) — 5 tiles, same fill-bar glyph as per-action
// effort (SS6.1), independent data value.
// ---------------------------------------------------------------------------

const EFFORT_OPTIONS: ReadonlyArray<{ readonly value: ObjectiveLevelOfEffort; readonly label: string; readonly bars: number }> = [
  { value: 'light', label: 'Light', bars: 1 },
  { value: 'steady', label: 'Steady', bars: 2 },
  { value: 'focused', label: 'Focused', bars: 3 },
  { value: 'heavy', label: 'Heavy', bars: 4 },
  { value: 'intense', label: 'Intense', bars: 5 },
];

export function EffortBars({ filled, total = 5 }: { readonly filled: number; readonly total?: number }) {
  return (
    <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px' }}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          style={{
            width: '3px',
            height: `${6 + i * 2}px`,
            borderRadius: '1px',
            background: i < filled ? 'var(--hue-primary-vivid)' : 'var(--border-subtle)',
          }}
        />
      ))}
    </span>
  );
}

export function EffortTileSelector({
  value,
  onChange,
}: {
  readonly value: ObjectiveLevelOfEffort | null;
  readonly onChange: (next: ObjectiveLevelOfEffort) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Level of effort" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--space-2)' }}>
      {EFFORT_OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`Level of effort: ${option.label}`}
            onClick={() => onChange(option.value)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-2)',
              minHeight: 'var(--touch-target-min)',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius-md)',
              border: selected ? '1px solid var(--hue-primary-vivid)' : '1px solid var(--border-subtle)',
              background: selected
                ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, var(--surface-raised))'
                : 'var(--surface-raised)',
            }}
          >
            <EffortBars filled={option.bars} />
            <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 500, color: selected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cadence stepper (SS5.3)
// ---------------------------------------------------------------------------

export function CadenceStepper({
  value,
  onChange,
}: {
  readonly value: number | null;
  readonly onChange: (next: number | null) => void;
}) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <button
          type="button"
          aria-label="Decrease cadence goal"
          onClick={() => onChange(Math.max(0, (value ?? 0) - 1))}
          disabled={value === 0}
          style={{
            width: 'var(--touch-target-min)',
            height: 'var(--touch-target-min)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--type-heading-size)',
          }}
        >
          −
        </button>
        <span className="num" style={{ minWidth: '48px', textAlign: 'center', fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-primary)' }}>
          {value === null ? '—' : value}
        </span>
        <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>/wk</span>
        <button
          type="button"
          aria-label="Increase cadence goal"
          onClick={() => onChange(Math.min(14, (value ?? 0) + 1))}
          disabled={value === 14}
          style={{
            width: 'var(--touch-target-min)',
            height: 'var(--touch-target-min)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--type-heading-size)',
          }}
        >
          +
        </button>
      </div>
      {value === 0 && (
        <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
          No stated pace goal — the forecast will still track your actual pace.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic segmented control (SS5.4/SS5.5) — no color-coding by value.
// ---------------------------------------------------------------------------

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  readonly label: string;
  readonly options: ReadonlyArray<{ readonly value: T; readonly label: string }>;
  readonly value: T;
  readonly onChange: (next: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: 'flex',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        overflow: 'hidden',
      }}
    >
      {options.map((option, i) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={`${label}: ${option.label}`}
            onClick={() => onChange(option.value)}
            style={{
              flex: 1,
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              background: selected ? 'var(--surface-hover)' : 'transparent',
              color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: selected ? 600 : 500,
              fontSize: 'var(--type-body-size)',
              borderRight: i !== options.length - 1 ? '1px solid var(--border-subtle)' : 'none',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function PrioritySegmentedControl({
  value,
  onChange,
}: {
  readonly value: ObjectivePriority;
  readonly onChange: (next: ObjectivePriority) => void;
}) {
  return (
    <SegmentedControl
      label="Priority"
      value={value}
      onChange={onChange}
      options={[
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ]}
    />
  );
}

/** Two-way on the edit path (Active/On hold only — no `active -> draft`
 *  transition exists); absent entirely on the create path (SS5.5). */
export function StatusSegmentedControl({
  value,
  onChange,
}: {
  readonly value: 'active' | 'on_hold';
  readonly onChange: (next: 'active' | 'on_hold') => void;
}) {
  return (
    <SegmentedControl
      label="Status"
      value={value}
      onChange={onChange}
      options={[
        { value: 'active', label: 'Active' },
        { value: 'on_hold', label: 'On hold' },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// Reminder schedule picker (SS5.6) — storage/display only, no delivery.
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatReminder(reminder: ObjectiveReminder | null): string {
  if (!reminder) return 'None';
  return `Every ${DAY_LABELS[reminder.dayOfWeek]} · ${reminder.time}`;
}

export function ReminderSchedulePicker({
  value,
  onChange,
}: {
  readonly value: ObjectiveReminder | null;
  readonly onChange: (next: ObjectiveReminder | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', handlePointerDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Reminder: ${formatReminder(value)}`}
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-sunken)',
          color: value ? 'var(--text-primary)' : 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>{formatReminder(value)}</span>
        <ChevronDown size={20} color="var(--text-muted)" aria-hidden="true" />
      </button>
      <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        Stored for now — this doesn&apos;t send a notification yet.
      </p>
      {open && (
        <ReminderPopover
          value={value}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function ReminderPopover({
  value,
  onSelect,
  onClose,
}: {
  readonly value: ObjectiveReminder | null;
  readonly onSelect: (next: ObjectiveReminder | null) => void;
  readonly onClose: () => void;
}) {
  const [dayOfWeek, setDayOfWeek] = useState<ObjectiveReminder['dayOfWeek']>(value?.dayOfWeek ?? 0);
  const [time, setTime] = useState(value?.time ?? '09:00');

  return (
    <div
      role="dialog"
      aria-label="Choose reminder schedule"
      style={{
        position: 'absolute',
        zIndex: 41,
        top: 'calc(100% + var(--space-2))',
        left: 0,
        right: 0,
        background: 'var(--surface-overlay)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-2)',
        border: '1px solid var(--border-subtle)',
        padding: 'var(--space-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <button
        type="button"
        onClick={() => onSelect(null)}
        style={{ textAlign: 'left', minHeight: 'var(--touch-target-min)', color: 'var(--text-secondary)', fontSize: 'var(--type-body-size)' }}
      >
        None
      </button>
      <select
        aria-label="Reminder day of week"
        value={dayOfWeek}
        onChange={(e) => setDayOfWeek(Number(e.target.value) as ObjectiveReminder['dayOfWeek'])}
        style={{
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-sunken)',
          color: 'var(--text-primary)',
          fontSize: 'var(--type-body-size)',
        }}
      >
        {DAY_LABELS.map((label, i) => (
          <option key={label} value={i}>
            {label}
          </option>
        ))}
      </select>
      <input
        aria-label="Reminder time"
        type="time"
        value={time}
        onChange={(e) => setTime(e.target.value)}
        style={{
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-sunken)',
          color: 'var(--text-primary)',
          fontSize: 'var(--type-body-size)',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={onClose}
          style={{ minHeight: 'var(--touch-target-min)', padding: '0 var(--space-3)', color: 'var(--text-secondary)', fontSize: 'var(--type-caption-size)' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSelect({ dayOfWeek, time })}
          style={{
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--hue-primary-vivid)',
            color: 'var(--text-on-accent)',
            fontWeight: 600,
            fontSize: 'var(--type-caption-size)',
          }}
        >
          Set reminder
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Autosave-status pill (SS2)
// ---------------------------------------------------------------------------

export type AutosaveState =
  | { readonly kind: 'draft_unsaved' }
  | { readonly kind: 'draft_saved'; readonly secondsAgo: number }
  | { readonly kind: 'active_saved'; readonly secondsAgo: number | null }
  | { readonly kind: 'receipt'; readonly message: string };

export function AutosaveStatusPill({ state }: { readonly state: AutosaveState }) {
  if (state.kind === 'receipt') {
    return (
      <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--text-primary)' }}>{state.message}</span>
    );
  }

  const dotColor = state.kind === 'active_saved' ? 'var(--hue-primary-vivid)' : 'var(--state-caution)';
  let label: string;
  if (state.kind === 'draft_unsaved') {
    label = 'Draft · saving...';
  } else if (state.kind === 'draft_saved') {
    label = `Draft · autosaved ${state.secondsAgo}s ago`;
  } else {
    label = state.secondsAgo === null ? 'Saved' : `Saved ${Math.floor(state.secondsAgo / 60) > 0 ? `${Math.floor(state.secondsAgo / 60)}m ago` : 'just now'}`;
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        fontSize: 'var(--type-caption-size)',
        fontWeight: 600,
        color: 'var(--text-secondary)',
        background: 'var(--surface-hover)',
        borderRadius: 'var(--radius-pill)',
        padding: '2px 10px',
      }}
    >
      <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: dotColor, flex: 'none' }} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Small picker-option row, shared by the reminder/link popovers.
// ---------------------------------------------------------------------------

export function PickerOptionRow({
  label,
  selected,
  onSelect,
}: {
  readonly label: ReactNode;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 'var(--touch-target-min)',
        width: '100%',
        padding: '0 var(--space-3)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-primary)',
        fontSize: 'var(--type-body-size)',
        textAlign: 'left',
      }}
    >
      <span>{label}</span>
      {selected && <Check size={16} color="var(--hue-primary)" aria-hidden="true" />}
    </button>
  );
}
